import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Upload, FileText, Trash2, Sparkles, Check, X, AlertTriangle,
    TrendingUp, Pencil, ChevronLeft,
} from 'lucide-react';
import { refertiAPI } from '../../services/refertiApi';
import api, { visiteAPI } from '../../services/api';
import TabellaEsami from './TabellaEsami';
import { apriFile } from '../../services/fileUrl';
import '../../styles/EsamiReferti.css';
import '../../styles/EsamiRefertiConsole.css';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';

/**
 * EsamiRefertiTab — archivio dei referti e valori degli esami ematici.
 *
 * QUATTRO VISTE, UNA ALLA VOLTA
 *   tabella    la tabella cumulativa, la prima che si vede (Alex, 28/09/2026)
 *   elenco     i referti archiviati, e l'area di caricamento
 *   dettaglio  un referto: i suoi valori, da rivedere e correggere
 *   andamento  un parametro nel tempo
 *
 * COSA E' VERIFICATO E COSA NO
 * Un valore estratto nasce NON verificato e si vede: pallino vuoto
 * nell'elenco, punto cavo nel grafico. Finche' nessuno l'ha guardato non
 * e' un dato su cui prendere decisioni, e mostrarlo uguale a uno
 * confermato sarebbe il modo piu' rapido per far fidare qualcuno di un
 * numero che la macchina ha letto storto.
 */

const CATEGORIE = [
    { v: 'laboratorio',          t: 'Esami di laboratorio' },
    { v: 'radiologia',           t: 'Radiologia' },
    { v: 'ecografia',            t: 'Ecografia' },
    { v: 'istologico',           t: 'Istologico' },
    { v: 'strumentale',          t: 'Esame strumentale' },
    { v: 'visita_specialistica', t: 'Visita specialistica' },
    { v: 'altro',                t: 'Altro' },
];

const nomeCategoria = (v) => (CATEGORIE.find((c) => c.v === v) || {}).t || v;

const STATO = {
    non_richiesta: { t: 'archiviato', c: 'neutro' },
    in_corso:      { t: 'estrazione in corso', c: 'attesa' },
    riuscita:      { t: 'valori estratti', c: 'buono' },
    parziale:      { t: 'estratto, da riconoscere', c: 'attenzione' },
    fallita:       { t: 'estrazione non riuscita', c: 'guasto' },
    opposizione:   { t: 'solo archiviato (opposizione)', c: 'neutro' },
};

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};

const num = (v, dec) => {
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return '—';
    return n.toLocaleString('it-IT', {
        minimumFractionDigits: 0,
        maximumFractionDigits: dec != null ? dec : 3,
    });
};

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

/* ------------------------------------------------------------------ */
/* Il grafico                                                          */
/*                                                                     */
/* Disegnato a mano in SVG invece che con una libreria: sono una serie  */
/* di punti e una banda di riferimento, e una dipendenza in piu' nel    */
/* pacchetto va poi mantenuta per anni.                                 */
/* ------------------------------------------------------------------ */

const Grafico = ({ serie, unita }) => {
    const dati = serie.filter((s) => s.valore_num != null && s.data_esame);
    if (dati.length < 2) return null;

    const L = 46, R = 14, T = 14, B = 26, W = 640, H = 200;
    const punti = dati.map((s) => ({
        x: new Date(s.data_esame).getTime(),
        y: parseFloat(s.valore_num),
        verificato: s.verificato,
        data: s.data_esame,
    }));

    const min = Math.min(...dati.map((s) => parseFloat(s.riferimento_min ?? s.valore_num)),
                         ...punti.map((p) => p.y));
    const max = Math.max(...dati.map((s) => parseFloat(s.riferimento_max ?? s.valore_num)),
                         ...punti.map((p) => p.y));
    const margine = (max - min) * 0.15 || 1;
    const yMin = min - margine, yMax = max + margine;

    const xMin = Math.min(...punti.map((p) => p.x));
    const xMax = Math.max(...punti.map((p) => p.x));
    const px = (x) => L + ((x - xMin) / (xMax - xMin || 1)) * (W - L - R);
    const py = (y) => T + (1 - (y - yMin) / (yMax - yMin || 1)) * (H - T - B);

    // La banda di normalita', presa dall'ultimo referto che la riporta
    const conBanda = [...dati].reverse()
        .find((s) => s.riferimento_min != null || s.riferimento_max != null);
    const bandaMin = conBanda?.riferimento_min != null ? parseFloat(conBanda.riferimento_min) : null;
    const bandaMax = conBanda?.riferimento_max != null ? parseFloat(conBanda.riferimento_max) : null;

    const linea = punti.map((p, i) => `${i ? 'L' : 'M'}${px(p.x)},${py(p.y)}`).join(' ');

    return (
        <svg className="esr-grafico" viewBox={`0 0 ${W} ${H}`} role="img"
             aria-label={`Andamento nel tempo, ${unita || ''}`}>
            {bandaMin != null && bandaMax != null && (
                <rect x={L} y={py(bandaMax)} width={W - L - R}
                      height={Math.max(py(bandaMin) - py(bandaMax), 1)}
                      className="esr-banda" />
            )}
            <line x1={L} y1={py(yMin)} x2={W - R} y2={py(yMin)} className="esr-asse" />
            <line x1={L} y1={T} x2={L} y2={H - B} className="esr-asse" />

            <text x={L - 6} y={py(yMax) + 4} className="esr-tacca" textAnchor="end">
                {num(yMax, 1)}
            </text>
            <text x={L - 6} y={py(yMin) + 4} className="esr-tacca" textAnchor="end">
                {num(yMin, 1)}
            </text>

            <path d={linea} className="esr-linea" />

            {punti.map((p, i) => (
                <g key={i}>
                    <circle cx={px(p.x)} cy={py(p.y)} r="4.5"
                            /* Il grafico tiene i valori e perde il rosso: mostra
                               l'andamento, non dice se e' giusto. */
                            className="esr-punto"
                            /* Cavo = non verificato da una persona */
                            fill={p.verificato ? undefined : '#fff'} />
                    <title>{`${dataIta(p.data)}: ${num(p.y)} ${unita || ''}`
                            + (p.verificato ? '' : ' — non verificato')}</title>
                </g>
            ))}

            <text x={L} y={H - 8} className="esr-tacca">{dataIta(punti[0].data)}</text>
            <text x={W - R} y={H - 8} className="esr-tacca" textAnchor="end">
                {dataIta(punti[punti.length - 1].data)}
            </text>
        </svg>
    );
};

/* ------------------------------------------------------------------ */

const EsamiRefertiTab = ({ pazienteId }) => {
    const [vista, setVista] = useState('tabella');   // tabella | elenco | dettaglio | andamento
    const [versioneTabella, setVersioneTabella] = useState(0);
    /* Il medico destinatario del referto (migration 267): la sua opzione
       decide se l'estrazione parte da sola. Di partenza, il medico
       dell'ultima visita del paziente. */
    const [medici, setMedici] = useState([]);
    const [destinatarioIniziale, setDestinatarioIniziale] = useState('');
    const [referti, setReferti] = useState([]);
    /* Quanti caratteri tengono Struttura, Medico refertante e Descrizione:
       li manda il server insieme all'elenco, letti dalle colonne, cosi' il
       numero sta in un posto solo. Servono all'AVVISO, non a un maxLength
       (revisione del beta su 33eaa5d, 7/10/2026): maxLength taglia in
       silenzio un testo incollato - 350 caratteri restavano 300 e si
       archiviavano cosi' - e la regola e' quella del ramo
       formato-intestatari: niente troncamento silenzioso. Il testo resta
       intero, l'avviso dice quale campo e di quanto, e l'arbitro resta il
       server, che rifiuta (400) col nome del campo prima di archiviare il
       file. Senza limiti (un server di prima) niente avviso, e il server
       rifiuta lo stesso. */
    const [limiti, setLimiti] = useState({});
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [inCorso, setInCorso] = useState(false);
    const [filtro, setFiltro] = useState('');

    // caricamento
    const [sopra, setSopra] = useState(false);
    const [file, setFile] = useState(null);
    const [form, setForm] = useState({
        categoria: 'laboratorio', data_esame: '', struttura: '',
        medico_esterno: '', descrizione: '', note: '', medico_destinatario_id: '',
    });
    useEffect(() => {
        api.get('/medici', { params: { soloAttivi: 'true' } })
            .then((r) => { const d = r.data?.data; setMedici(Array.isArray(d) ? d : (d?.medici || [])); })
            .catch(() => setMedici([]));
        visiteAPI.getByPazienteId(pazienteId)
            .then((r) => {
                const v = (r.data?.data?.visite || []).filter((x) => x.medico_id)
                    .sort((a, b) => String(b.data_visita).localeCompare(String(a.data_visita)))[0];
                const id = v ? String(v.medico_id) : '';
                setDestinatarioIniziale(id);
                setForm((f) => (f.medico_destinatario_id ? f : { ...f, medico_destinatario_id: id }));
            })
            .catch(() => {});
    }, [pazienteId]);

    // dettaglio
    const [aperto, setAperto] = useState(null);
    const [valori, setValori] = useState([]);
    const [analiti, setAnaliti] = useState([]);
    const [inModifica, setInModifica] = useState(null);

    // andamento
    const [parametri, setParametri] = useState([]);
    const [scelto, setScelto] = useState(null);
    const [serie, setSerie] = useState(null);

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            const r = await refertiAPI.perPaziente(pazienteId);
            setReferti(r.data.data.referti || []);
            setLimiti(r.data.data.limiti || {});
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare i referti'));
        } finally {
            setCaricamento(false);
        }
    }, [pazienteId]);

    useEffect(() => { carica(); }, [carica]);

    useEffect(() => {
        refertiAPI.analiti().then((r) => setAnaliti(r.data.data.analiti || []))
            .catch(() => {});
    }, []);

    /* ---------- caricamento ---------- */

    /* Un file scelto con i suoi dati compilati e non ancora caricato e'
       lavoro che si perde: si avvisa all'uscita. Il salvataggio azzera il
       modulo (versioneModulo sale) e la sporcizia con lui. */
    const [versioneModulo, setVersioneModulo] = useState(0);
    const sporco = useSporco({ file: file ? file.name : null, form }, versioneModulo);
    useUscitaProtetta(sporco);

    /* I campi oltre il limite della colonna, col nome della schermata. I
       caratteri si contano come li conta PostgreSQL (e il server: vedi
       lunghezzeColonne), per punto di codice. */
    const oltreIlLimite = [
        ['struttura', 'Struttura'], ['medico_esterno', 'Medico refertante'], ['descrizione', 'Descrizione'],
    ].filter(([k]) => limiti[k] && Array.from(form[k] || '').length > limiti[k])
        .map(([k, nome]) => `Il campo «${nome}» può avere al massimo ${limiti[k]} caratteri: `
            + `quello scritto ne ha ${Array.from(form[k] || '').length}.`);

    const salva = async () => {
        if (!file) return;
        try {
            setErrore(null);
            setInCorso(true);
            const fd = new FormData();
            fd.append('file', file);
            Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
            const r = await refertiAPI.carica(pazienteId, fd);
            setAvviso(r.data.message);
            setFile(null);
            setForm({ categoria: 'laboratorio', data_esame: '', struttura: '',
                      medico_esterno: '', descrizione: '', note: '', medico_destinatario_id: destinatarioIniziale });
            setVersioneModulo((v) => v + 1);
            setVersioneTabella((v) => v + 1);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel caricamento'));
        } finally {
            setInCorso(false);
        }
    };

    /* ---------- dettaglio ---------- */

    const apri = async (r) => {
        try {
            setErrore(null);
            const d = await refertiAPI.dettaglio(r.id);
            setAperto(d.data.data.referto);
            setValori(d.data.data.valori || []);
            setVista('dettaglio');
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile aprire il referto'));
        }
    };

    /* LA LETTURA NON FINISCE QUANDO FINISCE LA RICHIESTA.
       Dal 20 settembre 2026 il server mette il lavoro in coda e risponde
       subito: la risposta dice «ho cominciato», non «ho finito». Prima
       aspettava dentro la richiesta - nove secondi sull'unica scansione
       in archivio, una cinquantina su una da dieci pagine - e chi
       premeva concludeva che fosse bloccato.

       Quindi si guarda finche' lo stato smette di essere «in corso».
       Ogni due secondi: sotto si chiederebbe piu' del necessario, sopra
       si aspetterebbe con il risultato gia' pronto. Il limite di tre
       minuti non ferma il lavoro - quello va avanti per conto suo - ma
       smette di guardare e lo dice, invece di lasciare una rotella che
       gira per sempre. */
    const attendi = async (id) => {
        const FINO_A = Date.now() + 3 * 60 * 1000;
        while (Date.now() < FINO_A) {
            await new Promise((r) => setTimeout(r, 2000));
            const d = await refertiAPI.dettaglio(id);
            const ref = d.data.data.referto;
            if (ref.stato_estrazione !== 'in_corso') {
                setAperto(ref);
                setValori(d.data.data.valori || []);
                await carica();
                return ref;
            }
        }
        return null;
    };

    const estrai = async (id) => {
        try {
            setErrore(null);
            setAvviso(null);
            setInCorso(true);
            const r = await refertiAPI.estrai(id);
            if (!r.data.success) {
                setErrore(r.data.message);
                return;
            }
            setAvviso(r.data.message);
            await carica();

            const ref = await attendi(id);
            if (!ref) {
                setAvviso('La lettura sta ancora andando avanti. Puoi chiudere: '
                        + 'i valori compariranno qui quando è finita.');
            } else if (ref.stato_estrazione === 'fallita' || ref.stato_estrazione === 'opposizione') {
                setAvviso(null);
                setErrore(ref.errore_estrazione || 'La lettura non è riuscita.');
            } else {
                setAvviso('Lettura completata. I valori vanno rivisti prima di '
                        + 'considerarli buoni.');
            }
        } catch (e) {
            setErrore(messaggio(e, 'Errore nell\'estrazione'));
        } finally {
            setInCorso(false);
        }
    };

    const salvaValore = async (v) => {
        try {
            setErrore(null);
            await refertiAPI.aggiornaValore(v.id, {
                analita_id: v.analita_id || null,
                valore_num: v.valore_num,
                valore_testo: v.valore_testo,
                unita_grezza: v.unita_grezza,
                riferimento_min: v.riferimento_min,
                riferimento_max: v.riferimento_max,
            });
            const d = await refertiAPI.dettaglio(aperto.id);
            setValori(d.data.data.valori || []);
            setInModifica(null);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio del valore'));
        }
    };

    const confermaTutti = async () => {
        try {
            const r = await refertiAPI.verificaTutti(aperto.id);
            setAvviso(r.data.message);
            const d = await refertiAPI.dettaglio(aperto.id);
            setValori(d.data.data.valori || []);
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella conferma'));
        }
    };

    const elimina = async (r) => {
        if (!window.confirm(`Rimuovere il referto del ${dataIta(r.data_esame)}? `
            + 'Resta nello storico ma non sarà più visibile in scheda.')) return;
        try {
            await refertiAPI.elimina(r.id);
            setAvviso('Referto rimosso.');
            setVista('elenco');
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella rimozione'));
        }
    };

    /* ---------- andamento ---------- */

    const apriAndamento = async (codice = null) => {
        try {
            setErrore(null);
            const r = await refertiAPI.andamento(pazienteId);
            const p = r.data.data.parametri || [];
            setParametri(p);
            setVista('andamento');
            /* Dalla tabella si arriva con l'analita della riga. */
            if (codice && p.some((x) => x.analita_codice === codice)) await mostraSerie(codice);
            else if (p.length) await mostraSerie(p[0].analita_codice);
            else setSerie(null);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare l\'andamento'));
        }
    };

    const mostraSerie = async (codice) => {
        try {
            setScelto(codice);
            const r = await refertiAPI.andamento(pazienteId, codice);
            setSerie(r.data.data);
        } catch (e) {
            setErrore(messaggio(e, 'Impossibile caricare la serie'));
        }
    };

    const visibili = useMemo(
        () => (filtro ? referti.filter((r) => r.categoria === filtro) : referti),
        [referti, filtro]
    );

    const daVerificare = valori.filter((v) => !v.verificato_il).length;

    /* ---------- rendering ---------- */

    if (caricamento) return <div className="esr-attesa">Caricamento…</div>;

    return (
        <div className="esr-wrap">
            {errore && (
                <div className="esr-errore">
                    <AlertTriangle size={15} /> {errore}
                    <button onClick={() => setErrore(null)}><X size={14} /></button>
                </div>
            )}
            {avviso && (
                <div className="esr-avviso">
                    <Check size={15} /> {avviso}
                    <button onClick={() => setAvviso(null)}><X size={14} /></button>
                </div>
            )}

            {(vista === 'tabella' || vista === 'elenco') && (
                <div className="esr-viste" role="tablist">
                    <button type="button" role="tab" className={vista === 'tabella' ? 'attiva' : ''}
                            onClick={() => setVista('tabella')}>Tabella degli esami</button>
                    <button type="button" role="tab" className={vista === 'elenco' ? 'attiva' : ''}
                            onClick={() => setVista('elenco')}>Referti e caricamento</button>
                </div>
            )}

            {/* ============ TABELLA CUMULATIVA ============ */}
            {vista === 'tabella' && (
                <TabellaEsami pazienteId={pazienteId} versione={versioneTabella}
                              onVerifica={(id) => apri({ id })}
                              onAndamento={(codice) => apriAndamento(codice)} />
            )}

            {/* ============ ELENCO ============ */}
            {vista === 'elenco' && (
                <>
                    <div
                        className={`esr-area ${sopra ? 'sopra' : ''} ${file ? 'pieno' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); setSopra(true); }}
                        onDragLeave={() => setSopra(false)}
                        onDrop={(e) => {
                            e.preventDefault(); setSopra(false);
                            const f = e.dataTransfer.files?.[0];
                            if (f) setFile(f);
                        }}
                    >
                        <Upload size={22} />
                        {file ? (
                            <p className="esr-file">
                                {file.name}
                                <button onClick={() => setFile(null)} aria-label="Togli">
                                    <X size={14} />
                                </button>
                            </p>
                        ) : (
                            <p>
                                Trascina qui il referto, oppure{' '}
                                <label className="esr-scegli">
                                    scegli un file
                                    <input type="file" accept="application/pdf,image/*"
                                           onChange={(e) => setFile(e.target.files?.[0] || null)} />
                                </label>
                            </p>
                        )}
                    </div>

                    {file && (
                        <div className="esr-form">
                            <div className="esr-riga">
                                <label>
                                    <span>Tipo di esame *</span>
                                    <select value={form.categoria}
                                            onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                                        {CATEGORIE.map((c) => (
                                            <option key={c.v} value={c.v}>{c.t}</option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    <span>Data dell&apos;esame</span>
                                    <input type="date" value={form.data_esame}
                                           onChange={(e) => setForm({ ...form, data_esame: e.target.value })} />
                                </label>
                            </div>
                            <div className="esr-riga">
                                <label>
                                    <span>Struttura</span>
                                    <input value={form.struttura} placeholder="Laboratorio, ospedale…"
                                           onChange={(e) => setForm({ ...form, struttura: e.target.value })} />
                                </label>
                                <label>
                                    <span>Per il medico</span>
                                    <select value={form.medico_destinatario_id}
                                            onChange={(e) => setForm({ ...form, medico_destinatario_id: e.target.value })}>
                                        <option value="">Nessuno</option>
                                        {medici.map((m) => (
                                            <option key={m.id} value={m.id}>{[m.titolo, m.cognome, m.nome].filter(Boolean).join(' ')}</option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    <span>Medico refertante</span>
                                    <input value={form.medico_esterno}
                                           onChange={(e) => setForm({ ...form, medico_esterno: e.target.value })} />
                                </label>
                            </div>
                            <label>
                                <span>Descrizione</span>
                                <input value={form.descrizione} placeholder="Emocromo completo, RX torace…"
                                       onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />
                            </label>
                            <label>
                                <span>Note</span>
                                <textarea rows="2" value={form.note}
                                          onChange={(e) => setForm({ ...form, note: e.target.value })} />
                            </label>
                            {form.categoria === 'laboratorio' && (
                                <p className="esr-nota">
                                    Dopo l&apos;archiviazione si potranno estrarre i valori dal
                                    dettaglio del referto. I dati anagrafici del paziente
                                    vengono tolti dal testo prima dell&apos;elaborazione.
                                </p>
                            )}
                            {/* Accanto al pulsante, perche' e' li' che si capisce
                                perche' non va: il pulsante resta attivo e il
                                server rifiuta col nome del campo. */}
                            {oltreIlLimite.map((m) => (
                                <p key={m} className="esr-nota esr-nota--attenzione">
                                    <AlertTriangle size={14} /> {m} Accorcialo per archiviare il referto.
                                </p>
                            ))}
                            <div className="esr-azioni">
                                <button className="esr-btn" onClick={() => setFile(null)}>Annulla</button>
                                <button className="esr-btn esr-btn--principale"
                                        title={oltreIlLimite.length ? oltreIlLimite.join(' ') : undefined}
                                        onClick={salva} disabled={inCorso}>
                                    <Check size={15} /> {inCorso ? 'Carico…' : 'Archivia'}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="esr-testa-elenco">
                        <div className="esr-filtri">
                            <button className={!filtro ? 'attivo' : ''} onClick={() => setFiltro('')}>
                                Tutti
                            </button>
                            {CATEGORIE.filter((c) => referti.some((r) => r.categoria === c.v)).map((c) => (
                                <button key={c.v} className={filtro === c.v ? 'attivo' : ''}
                                        onClick={() => setFiltro(c.v)}>
                                    {c.t}
                                </button>
                            ))}
                        </div>
                        {referti.some((r) => r.valori > 0) && (
                            <button className="esr-btn" onClick={() => apriAndamento()}>
                                <TrendingUp size={15} /> Andamento nel tempo
                            </button>
                        )}
                    </div>

                    {visibili.length === 0 ? (
                        <p className="esr-vuoto">
                            Nessun referto archiviato. Il primo si carica trascinando il
                            file qui sopra.
                        </p>
                    ) : (
                        <ul className="esr-lista">
                            {visibili.map((r) => {
                                const st = STATO[r.stato_estrazione] || STATO.non_richiesta;
                                return (
                                    <li key={r.id} className="esr-voce" onClick={() => apri(r)}>
                                        <FileText size={18} className="esr-icona-file" />
                                        <div className="esr-voce-testo">
                                            <div className="esr-voce-titolo">
                                                {r.descrizione || r.documento_titolo || 'Referto'}
                                            </div>
                                            <div className="esr-voce-sub">
                                                {dataIta(r.data_esame)} · {nomeCategoria(r.categoria)}
                                                {r.struttura ? ` · ${r.struttura}` : ''}
                                            </div>
                                        </div>
                                        <div className="esr-voce-stato">
                                            <span className={`esr-pillola ${st.c}`}>{st.t}</span>
                                            {r.da_verificare > 0 && (
                                                <span className="esr-pillola attenzione">
                                                    {r.da_verificare} da verificare
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </>
            )}

            {/* ============ DETTAGLIO ============ */}
            {vista === 'dettaglio' && aperto && (
                <>
                    <div className="esr-testata">
                        <button className="esr-indietro" onClick={() => { setVista('elenco'); setAperto(null); }}>
                            <ChevronLeft size={16} /> Referti
                        </button>
                        <div className="esr-testata-azioni">
                            <button className="esr-btn" onClick={() => apriFile(aperto.url)}>
                                <FileText size={15} /> Apri il PDF
                            </button>
                            {aperto.categoria === 'laboratorio' && (
                                <button className="esr-btn" onClick={() => estrai(aperto.id)} disabled={inCorso}>
                                    <Sparkles size={15} />
                                    {valori.length ? 'Rifai l\'estrazione' : 'Estrai i valori'}
                                </button>
                            )}
                            <button className="esr-btn esr-btn--rosso" onClick={() => elimina(aperto)}>
                                <Trash2 size={15} />
                            </button>
                        </div>
                    </div>

                    <div className="esr-scheda">
                        <h3>{aperto.descrizione || aperto.documento_titolo || 'Referto'}</h3>
                        <p className="esr-scheda-sub">
                            {dataIta(aperto.data_esame)} · {nomeCategoria(aperto.categoria)}
                            {aperto.struttura ? ` · ${aperto.struttura}` : ''}
                            {aperto.medico_esterno ? ` · ${aperto.medico_esterno}` : ''}
                        </p>
                        {aperto.note && <p className="esr-scheda-note">{aperto.note}</p>}
                        {aperto.errore_estrazione && (
                            <p className="esr-scheda-guasto">
                                <AlertTriangle size={14} /> {aperto.errore_estrazione}
                            </p>
                        )}
                    </div>

                    {valori.length > 0 && (
                        <>
                            {daVerificare > 0 && (
                                <div className="esr-da-verificare">
                                    <span>
                                        <AlertTriangle size={15} />
                                        {daVerificare} valori non ancora verificati da una persona.
                                        Finché non lo sono, non vanno usati per decidere.
                                    </span>
                                    <button className="esr-btn esr-btn--principale" onClick={confermaTutti}>
                                        <Check size={15} /> Ho controllato: conferma tutti
                                    </button>
                                </div>
                            )}

                            <table className="esr-valori">
                                <thead>
                                    <tr>
                                        <th />
                                        <th>Esame</th>
                                        <th className="num">Valore</th>
                                        <th>Riferimento</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {valori.map((v) => {
                                        const mod = inModifica?.id === v.id ? inModifica : null;
                                        /* IL VALORE NON SI GIUDICA (23 settembre 2026).
                                           Qui si confrontava il valore con l'intervallo e si
                                           coloravano di rosso quelli fuori. Un software che
                                           confronta un valore con un intervallo di normalita'
                                           e segnala lo scostamento fornisce informazioni per
                                           una decisione clinica: e' la regola 11 dell'MDR, e
                                           RIMEDICHUB non e' un dispositivo medico perche'
                                           REGISTRA e non GIUDICA.
                                           L'intervallo si mostra lo stesso, come testo,
                                           accanto al valore: e' il riferimento STAMPATO DAL
                                           LABORATORIO, cioe' un dato del referto, non un
                                           giudizio nostro. Chi legge fa il confronto da se',
                                           che e' il suo mestiere.
                                           In piu' il confronto non guardava `verificato_il`:
                                           le righe «estratto, non ancora verificato» venivano
                                           segnalate come le altre, con un intervallo letto da
                                           una scansione. */
                                        return (
                                            <tr key={v.id} className={v.verificato_il ? '' : 'non-verificato'}>
                                                <td className="esr-spia">
                                                    <span
                                                        className={`esr-pallino ${v.verificato_il ? 'pieno' : ''}`}
                                                        title={v.verificato_il
                                                            ? `Verificato${v.verificato_cognome ? ' da ' + v.verificato_cognome : ''}`
                                                            + (v.corretto ? ', corretto a mano' : '')
                                                            : 'Estratto, non ancora verificato'}
                                                    />
                                                </td>
                                                <td>
                                                    {mod ? (
                                                        <select value={mod.analita_id || ''}
                                                                onChange={(e) => setInModifica({
                                                                    ...mod,
                                                                    analita_id: e.target.value ? parseInt(e.target.value, 10) : null,
                                                                })}>
                                                            <option value="">— non riconosciuto —</option>
                                                            {analiti.map((a) => (
                                                                <option key={a.id} value={a.id}>{a.nome}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <>
                                                            {v.analita_nome || v.sigla_grezza || '—'}
                                                            <span className="esr-sub">
                                                                {v.sigla_grezza}
                                                                {!v.analita_id && ' · da riconoscere'}
                                                            </span>
                                                        </>
                                                    )}
                                                </td>
                                                <td className="num">
                                                    {mod ? (
                                                        <input className="esr-mini"
                                                               value={mod.valore_num ?? mod.valore_testo ?? ''}
                                                               onChange={(e) => {
                                                                   const t = e.target.value;
                                                                   const n = parseFloat(t.replace(',', '.'));
                                                                   setInModifica({
                                                                       ...mod,
                                                                       valore_num: Number.isFinite(n) ? n : null,
                                                                       valore_testo: Number.isFinite(n) ? null : t,
                                                                   });
                                                               }} />
                                                    ) : (
                                                        <>
                                                            {v.valore_num != null ? num(v.valore_num) : (v.valore_testo || '—')}
                                                            <span className="esr-sub">{v.unita_grezza}</span>
                                                        </>
                                                    )}
                                                </td>
                                                <td className="esr-rif">
                                                    {v.riferimento_testo || '—'}
                                                </td>
                                                <td className="num">
                                                    {mod ? (
                                                        <>
                                                            <button className="esr-icona" onClick={() => salvaValore(mod)}
                                                                    title="Salva e conferma">
                                                                <Check size={15} />
                                                            </button>
                                                            <button className="esr-icona" onClick={() => setInModifica(null)}>
                                                                <X size={15} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <button className="esr-icona" title="Correggi"
                                                                onClick={() => setInModifica({ ...v })}>
                                                            <Pencil size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}

                    {aperto.categoria === 'laboratorio' && valori.length === 0 && (
                        <p className="esr-vuoto">
                            Nessun valore estratto. Se il referto è una scansione il testo
                            non si può leggere: resta archiviato come documento.
                        </p>
                    )}
                </>
            )}

            {/* ============ ANDAMENTO ============ */}
            {vista === 'andamento' && (
                <>
                    <div className="esr-testata">
                        <button className="esr-indietro" onClick={() => setVista('elenco')}>
                            <ChevronLeft size={16} /> Referti
                        </button>
                    </div>

                    {parametri.length === 0 ? (
                        <p className="esr-vuoto">
                            Nessun parametro con valori numerici. Servono referti di
                            laboratorio con i valori estratti.
                        </p>
                    ) : (
                        <>
                            <label className="esr-scelta">
                                <span>Parametro</span>
                                <select value={scelto || ''} onChange={(e) => mostraSerie(e.target.value)}>
                                    {parametri.map((p) => (
                                        <option key={p.analita_codice} value={p.analita_codice}>
                                            {p.analita_nome} ({p.misure} {p.misure === 1 ? 'misura' : 'misure'})
                                        </option>
                                    ))}
                                </select>
                            </label>

                            {serie && (
                                <>
                                    {serie.unita_miste && (
                                        <p className="esr-nota esr-nota--attenzione">
                                            <AlertTriangle size={14} /> I referti riportano unità
                                            diverse ({serie.unita_presenti.join(', ')}). I valori
                                            NON sono stati convertiti: confrontarli fra loro
                                            richiede attenzione.
                                        </p>
                                    )}
                                    {serie.serie.length < 2 ? (
                                        <p className="esr-vuoto">
                                            Serve più di una misura per vedere un andamento.
                                        </p>
                                    ) : (
                                        <Grafico serie={serie.serie}
                                                 unita={serie.serie[0]?.unita_grezza} />
                                    )}
                                    <table className="esr-valori">
                                        <thead>
                                            <tr>
                                                <th />
                                                <th>Data</th>
                                                <th className="num">Valore</th>
                                                <th>Riferimento</th>
                                                <th>Struttura</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {[...serie.serie].reverse().map((s) => (
                                                <tr key={s.valore_id} className={s.verificato ? '' : 'non-verificato'}>
                                                    <td className="esr-spia">
                                                        <span className={`esr-pallino ${s.verificato ? 'pieno' : ''}`}
                                                              title={s.verificato ? 'Verificato' : 'Non verificato'} />
                                                    </td>
                                                    <td>{dataIta(s.data_esame)}</td>
                                                    <td className="num">
                                                        {num(s.valore_num)}
                                                        <span className="esr-sub">{s.unita_grezza}</span>
                                                    </td>
                                                    <td className="esr-rif">{s.riferimento_testo || '—'}</td>
                                                    <td className="esr-rif">{s.struttura || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default EsamiRefertiTab;
